import "@/App.css";
import "@/i18n";
import "@/lib/look";
import React, { lazy, Suspense } from "react";

// Recover from stale code-split chunks after a new deploy: if a lazy chunk 404s
// (old hashed file removed from server), reload once to fetch the fresh index.html.
const CHUNK_RELOAD_KEY = "lattice_chunk_reloaded";
function lazyRetry(factory) {
  return lazy(() =>
    factory().catch((err) => {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }),
  );
}
import { BrowserRouter, Routes, Route, useLocation, useNavigate as useRawNavigate } from "react-router-dom";
import { Navigate } from "@/lib/nav";
import i18nInstance from "@/i18n";
import { motion } from "framer-motion";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { isAdminRole, isOwner as isOwnerLns } from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import AppBar from "@/components/AppBar";
import Home from "@/pages/Home";
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const SecuritySettings = lazyRetry(() => import("@/pages/SecuritySettings"));
const Explorer = lazyRetry(() => import("@/pages/Explorer"));
const Verifica = lazyRetry(() => import("@/pages/Verifica"));
const Certify = lazyRetry(() => import("@/pages/Certify"));
const Pulse = lazyRetry(() => import("@/pages/Pulse"));
const Lns = lazyRetry(() => import("@/pages/Lns"));
const Login = lazyRetry(() => import("@/pages/Login"));
const Onboarding = lazyRetry(() => import("@/pages/Onboarding"));
const Workspace = lazyRetry(() => import("@/pages/Workspace"));
const WebApp = lazyRetry(() => import("@/pages/WebApp"));
const PublicPost = lazyRetry(() => import("@/pages/PublicPost"));
const PublicHubPost = lazyRetry(() => import("@/pages/PublicHubPost"));
const License = lazyRetry(() => import("@/pages/License"));
const SuperAdmin = lazyRetry(() => import("@/pages/SuperAdmin"));
const Console = lazyRetry(() => import("@/pages/Console"));
import LicenseGate, { ModuleGuard } from "@/components/LicenseGate";
import FirstAccessGate from "@/components/FirstAccessGate";
import TrialBanner from "@/components/TrialBanner";
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
import VaultUnlock from "@/components/VaultUnlock";
import { loadThreatList } from "@/lib/threatList";

function VaultGate() {
  const { isAuth, locked } = useAuth();
  if (isAuth && locked) return <VaultUnlock />;
  return null;
}
const Whitepaper = lazyRetry(() => import("@/pages/Whitepaper"));
const YellowPaper = lazyRetry(() => import("@/pages/YellowPaper"));
const ThreatModel = lazyRetry(() => import("@/pages/ThreatModel"));
const QbftProof = lazyRetry(() => import("@/pages/QbftProof"));
const AuditReport = lazyRetry(() => import("@/pages/AuditReport"));
const RedTeam = lazyRetry(() => import("@/pages/RedTeam"));
const SecurityAudit = lazyRetry(() => import("@/pages/SecurityAudit"));
const Install = lazyRetry(() => import("@/pages/Install"));
const About = lazyRetry(() => import("@/pages/About"));
const UseCases = lazyRetry(() => import("@/pages/UseCases"));
const Demo = lazyRetry(() => import("@/pages/Demo"));
const Pricing = lazyRetry(() => import("@/pages/Pricing"));
const SignupPersonal = lazyRetry(() => import("@/pages/SignupPersonal"));
const WebSecurity = lazyRetry(() => import("@/pages/WebSecurity"));
const WebSignature = lazyRetry(() => import("@/pages/WebSignature"));
const NodeGuide = lazyRetry(() => import("@/pages/NodeGuide"));
const RolePage = lazyRetry(() => import("@/pages/RolePage"));
const ApiDocs = lazyRetry(() => import("@/pages/ApiDocs"));
const Compliance = lazyRetry(() => import("@/pages/Compliance"));
const Documenti = lazyRetry(() => import("@/pages/Documenti"));
const LegalIndex = lazyRetry(() => import("@/pages/legal/LegalIndex"));
const Terms = lazyRetry(() => import("@/pages/legal/Terms"));
const Privacy = lazyRetry(() => import("@/pages/legal/Privacy"));
const Dpa = lazyRetry(() => import("@/pages/legal/Dpa"));
const CookiePolicy = lazyRetry(() => import("@/pages/legal/CookiePolicy"));
const Sla = lazyRetry(() => import("@/pages/legal/Sla"));
const Eula = lazyRetry(() => import("@/pages/legal/Eula"));
const Aup = lazyRetry(() => import("@/pages/legal/Aup"));

function Page({ children }) {
  return children;
}

// Tabula Rasa: le vecchie pagine marketing sono state rimosse dal sito pubblico. Chi
// arriva su una di quelle rotte viene riportato al Gateway (homepage vanilla su "/").
function GatewayRedirect() {
  React.useEffect(() => { window.location.replace("/"); }, []);
  return null;
}

/// Un rimando altrove, detto in una riga. `GatewayRedirect` porta sempre alla home: qui
/// serve portare DOVE si può fare la cosa che l'utente stava cercando.
function Verso({ dove }) {
  React.useEffect(() => { window.location.replace(dove); }, [dove]);
  return null;
}

const P = (el) => <Page>{el}</Page>;
const PR = (el) => <ProtectedRoute><FirstAccessGate><Page>{el}</Page></FirstAccessGate></ProtectedRoute>;
const LR = (el) => <ProtectedRoute><FirstAccessGate><LicenseGate><Page>{el}</Page></LicenseGate></FirstAccessGate></ProtectedRoute>;

// Dashboard/Explorer: public when logged out; logged-in employees (non-admin) are redirected to /app
function AdminGate({ children }) {
  const { isAuth, user, ready } = useAuth();
  if (!ready) return null;
  if (isAuth && !isAdminRole(user.role)) return <Navigate to="/app" replace />;
  return children;
}
const AG = (el) => <AdminGate><Page>{el}</Page></AdminGate>;

// Console: strictly the platform owner (fabioastorino.lattice.lns).
function OwnerGate({ children }) {
  const { isAuth, user, ready } = useAuth();
  if (!ready) return null;
  if (!isAuth || !isOwnerLns(user)) return <Navigate to="/app" replace />;
  return children;
}
const OG = (el) => <ProtectedRoute><OwnerGate><Page>{el}</Page></OwnerGate></ProtectedRoute>;

function AnimatedRoutes() {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);
  return (
    <motion.div key={location.pathname}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}>
      <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
      <Routes location={location}>
        <Route index element={<GatewayRedirect />} />
        <Route path="dashboard" element={AG(<Dashboard />)} />
        <Route path="console" element={OG(<Console />)} />
        <Route path="explorer" element={P(<Explorer />)} />
        <Route path="verifica" element={P(<Verifica />)} />
        <Route path="whitepaper" element={<GatewayRedirect />} />
        <Route path="yellowpaper" element={<GatewayRedirect />} />
        <Route path="threat-model" element={<GatewayRedirect />} />
        <Route path="prova-qbft" element={<GatewayRedirect />} />
        <Route path="qbft-proof" element={<GatewayRedirect />} />
        <Route path="install" element={<GatewayRedirect />} />
        <Route path="security-audit" element={<GatewayRedirect />} />
        <Route path="audit/certificazione" element={<GatewayRedirect />} />
        <Route path="audit" element={<GatewayRedirect />} />
        <Route path="audit/red-team" element={<GatewayRedirect />} />
        <Route path="audit/ledger" element={<GatewayRedirect />} />
        <Route path="chi-siamo" element={<GatewayRedirect />} />
        <Route path="casi-duso" element={<GatewayRedirect />} />
        <Route path="demo" element={<GatewayRedirect />} />
        <Route path="prezzi" element={<GatewayRedirect />} />
        <Route path="accesso" element={<GatewayRedirect />} />
        <Route path="registrati" element={P(<SignupPersonal />)} />
        <Route path="sicurezza-web" element={<GatewayRedirect />} />
        <Route path="firma-web" element={<GatewayRedirect />} />
        <Route path="verifica" element={<GatewayRedirect />} />
        <Route path="guida-nodo" element={<GatewayRedirect />} />
        <Route path="per-il-cio" element={<GatewayRedirect />} />
        <Route path="per-il-ciso" element={<GatewayRedirect />} />
        <Route path="per-il-ceo" element={<GatewayRedirect />} />
        <Route path="sviluppatori" element={<GatewayRedirect />} />
        <Route path="conformita" element={<GatewayRedirect />} />
        <Route path="documenti" element={<GatewayRedirect />} />
        <Route path="legale" element={<GatewayRedirect />} />
        <Route path="termini" element={<GatewayRedirect />} />
        <Route path="privacy" element={<GatewayRedirect />} />
        <Route path="dpa" element={<GatewayRedirect />} />
        <Route path="cookie" element={<GatewayRedirect />} />
        <Route path="sla" element={<GatewayRedirect />} />
        <Route path="licenza" element={<GatewayRedirect />} />
        <Route path="uso-accettabile" element={<GatewayRedirect />} />
        <Route path="login" element={P(<Login />)} />
        {/* v8.0.0: `/public/trial-signup` risponde 410 da quando le registrazioni libere
            sono chiuse, quindi questo modulo poteva solo fallire. Ora porta dove la
            richiesta si può davvero mandare. */}
        <Route path="onboarding" element={<Verso dove="/aziende#richiesta" />} />
        <Route path="app" element={LR(<WebApp />)} />
        <Route path="workspace" element={LR(<Workspace />)} />
        <Route path="security" element={PR(<SecuritySettings />)} />
        <Route path="certify" element={LR(<Certify />)} />
        <Route path="pulse" element={LR(<ModuleGuard module="pulse" name="Pulse"><Pulse /></ModuleGuard>)} />
        <Route path="lns" element={LR(<Lns />)} />
        <Route path="license" element={PR(<License />)} />
        <Route path="super-admin" element={P(<SuperAdmin />)} />
        <Route path="post/:id" element={P(<PublicPost />)} />
        <Route path="hub/:id" element={P(<PublicHubPost />)} />
        <Route path="*" element={<GatewayRedirect />} />
      </Routes>
      </Suspense>
    </motion.div>
  );
}

// Keeps i18n language in sync with the URL and auto-detects English on first visit.
function LocaleController() {
  const location = useLocation();
  const navigate = useRawNavigate();
  React.useEffect(() => {
    const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
    const lang = isEn ? "en" : "it";
    if (i18nInstance.language !== lang) i18nInstance.changeLanguage(lang);
    localStorage.setItem("lattice_lang", lang);
  }, [location.pathname]);
  React.useEffect(() => {
    if (sessionStorage.getItem("lattice_lang_auto")) return;
    sessionStorage.setItem("lattice_lang_auto", "1");
    const p = window.location.pathname;
    if (p === "/en" || p.startsWith("/en/")) return;
    const stored = localStorage.getItem("lattice_lang");
    const prefersEn = stored ? stored === "en" : (navigator.language || "").toLowerCase().startsWith("en");
    if (prefersEn) navigate("/en" + (p === "/" ? "" : p) + window.location.search, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function LangRoutes() {
  return (
    <Routes>
      <Route path="/en/*" element={<AnimatedRoutes />} />
      <Route path="/*" element={<AnimatedRoutes />} />
    </Routes>
  );
}

/// Dentro /app si entra in Lattice: niente menu di prodotto, niente piede pagina con
/// prezzi e conformita', niente banner. Fuori, il sito pubblico resta quello che era.
function Chrome() {
  const { pathname } = useLocation();
  const inApp = /^\/(en\/)?app(\/|$)/.test(pathname);
  // Rotte funzionali/di accesso: niente header e footer del vecchio sito marketing.
  const bare = /^\/(en\/)?(login|onboarding|registrati|security|license|dashboard|console|explorer|certify|pulse|lns|super-admin|workspace)(\/|$)/.test(pathname);
  if (inApp) {
    return (
      <>
        <AppBar />
        <main><LangRoutes /></main>
      </>
    );
  }
  if (bare) {
    return <main><LangRoutes /></main>;
  }
  return (
    <>
      <Header />
      <TrialBanner />
      <main><LangRoutes /></main>
      <Footer />
      <CookieBanner />
    </>
  );
}

function App() {
  React.useEffect(() => {
    loadThreatList();
    const tl = setInterval(loadThreatList, 6 * 60 * 60 * 1000);
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    const onErr = (e) => {
      const msg = (e?.reason?.message || e?.message || "") + "";
      const name = (e?.reason?.name || "") + "";
      if (/ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(msg) || name === "ChunkLoadError") {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          window.location.reload();
        }
      }
    };
    window.addEventListener("unhandledrejection", onErr);
    window.addEventListener("error", onErr);
    return () => {
      clearInterval(tl);
      window.removeEventListener("unhandledrejection", onErr);
      window.removeEventListener("error", onErr);
    };
  }, []);
  return (
    <div className="min-h-screen bg-[#050505] text-[#f9f9f9]">
      <BrowserRouter>
        <LocaleController />
        <AuthProvider>
          <NotificationsProvider>
            <Chrome />
            <VaultGate />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
